/* FOM · workspace PROCESSOS — "como o trabalho acontece" (gate 0072).

   Cena de fluxo no formato do artefato aprovado: trilhos horizontais com
   ESTACOES na ordem real do `ordinal`, o objeto de handoff sob a etapa que e
   o seu pai REAL, uma faixa de TRANSICAO declarada como composicao da
   apresentacao, e os demais grupos de fluxo como trilhos seguintes.

   TUDO aqui deriva do dado: filhos `flow` de cada grupo (ordem do ordinal),
   relacoes reais anotadas com o tipo verbatim, provenance da transicao lida
   das relacoes (envia para / recebe). Seta tracejada = composicao visual,
   nunca relacao de banco — e a tela diz isso. Nada e criado ou mutado. */
import type { FomNode, Relation } from '../core/types.ts';
import { COR_ST, ROTULO_ST } from '../core/types.ts';
import { compararIrmaos } from '../core/layout.ts';

const NS = 'http://www.w3.org/2000/svg';
function el<K extends keyof SVGElementTagNameMap>(tag: K, attrs: Record<string, string | number> = {}): SVGElementTagNameMap[K] {
  const n = document.createElementNS(NS, tag);
  for (const [k, v] of Object.entries(attrs)) n.setAttribute(k, String(v));
  return n;
}
function txt(pai: SVGElement, x: number, y: number, s: string, classe: string, attrs: Record<string, string | number> = {}): SVGTextElement {
  const t = el('text', { x, y, class: classe, ...attrs });
  t.textContent = s;
  pai.appendChild(t);
  return t;
}
const trunc = (s: string, n: number): string => (s.length > n ? s.slice(0, n - 1) + '…' : s);

export type ProcessosOpcoes = { aoSelecionar: (externalId: string) => void };

/* Derivacao PURA da composicao de processos a partir do no de contexto:
   grupos = filhos `process` com filhos `flow`; a transicao e o grupo com
   step_kind /handoff/ em algum passo ou relacao 'relacionado a' real;
   o primario e o primeiro dos demais. */
export function derivarComposicao(nos: FomNode[], relacoes: Relation[], contextoId: string) {
  const ativos = nos.filter((n) => !n.removed_at);
  const filhosDe = (id: string): FomNode[] => ativos.filter((n) => n.parent_external_id === id).sort(compararIrmaos);
  const fluxoDe = (id: string): FomNode[] => filhosDe(id).filter((n) => n.child_kind === 'flow');
  const contexto = ativos.find((n) => n.external_id === contextoId) ?? null;
  const grupos = contexto
    ? filhosDe(contexto.external_id).filter((g) => (g.node_type ?? '') === 'process' && fluxoDe(g.external_id).length > 0)
    : [];
  const transicao = grupos.find((g) =>
    fluxoDe(g.external_id).some((p) => /handoff/i.test(p.step_kind ?? ''))
    || relacoes.some((r) => r.from === g.external_id && /relacionado/.test(r.type))) ?? null;
  const normais = grupos.filter((g) => g !== transicao);
  const primario = normais[0] ?? null;
  const demais = normais.slice(1);
  /* proveniencia REAL da transicao (nivel departamento): relacionado a → X;
     quem envia para X e o que X recebe */
  const alvoRel = transicao ? relacoes.find((r) => r.from === transicao.external_id && /relacionado/.test(r.type)) ?? null : null;
  const alvo = alvoRel ? ativos.find((n) => n.external_id === alvoRel.to) ?? null : null;
  const entra = alvo ? relacoes.find((r) => r.to === alvo.external_id && /envia/.test(r.type)) ?? null : null;
  const sai = alvo ? relacoes.find((r) => r.from === alvo.external_id && /recebe/.test(r.type)) ?? null : null;
  const de = entra ? ativos.find((n) => n.external_id === entra.from) ?? null : null;
  const para = sai ? ativos.find((n) => n.external_id === sai.to) ?? null : null;
  return { contexto, grupos, primario, transicao, demais, fluxoDe, filhosDe, alvoRel, alvo, entra, sai, de, para, ativos };
}

export function montarProcessos(host: HTMLElement, o: ProcessosOpcoes) {
  const raiz = document.createElement('div');
  raiz.className = 'processos-cena';
  raiz.hidden = true;
  host.appendChild(raiz);

  function render(nos: FomNode[], relacoes: Relation[], selecionado: string | null, contextoId: string): void {
    raiz.textContent = '';
    const c = derivarComposicao(nos, relacoes, contextoId);
    const W = 1180;
    let H = 560;
    const svg = el('svg', {
      class: 'scene proc-svg', viewBox: `0 0 ${W} ${H}`,
      preserveAspectRatio: 'xMidYMid meet',
      role: 'img', 'aria-label': 'Fluxo com estacoes, handoff e composicao',
    });
    const defs = el('defs');
    const mk = el('marker', { id: 'parr', viewBox: '0 0 10 10', refX: 9, refY: 5, markerWidth: 8, markerHeight: 8, orient: 'auto' });
    mk.appendChild(el('path', { d: 'M0 0L10 5L0 10z', class: 'parr' }));
    defs.appendChild(mk);
    svg.appendChild(defs);

    const estacao = (n: FomNode, x: number, y: number, r: number, i: number | null, nomeAcima: boolean, tamanhoNome: number): void => {
      const g = el('g', { class: 'station' + (selecionado === n.external_id ? ' sel' : ''), 'data-id': n.external_id, tabindex: 0, role: 'button', 'aria-label': `${n.name} — ${ROTULO_ST[n.st]}` });
      const circ = el('circle', { class: 'c', cx: x, cy: y, r, stroke: COR_ST[n.st] });
      if (n.st === 'ND') circ.setAttribute('stroke-dasharray', '3 3');
      g.appendChild(circ);
      if (i !== null) txt(g, x, y + 4, String(i), 'meta', { 'text-anchor': 'middle' });
      txt(g, x, nomeAcima ? y - r - 12 : y + r + 16, trunc(n.name, tamanhoNome), r >= 14 ? '' : 'meta nome', { 'text-anchor': 'middle' });
      const bits = `${n.st}${n.know.length ? ' · ◆' + n.know.length : ''}${n.gaps.length ? ' · ◇' + n.gaps.length : ''}`;
      if (r >= 11) txt(g, x, nomeAcima ? y + r + 22 : y - r - 10, bits, 'meta', { 'text-anchor': 'middle' });
      if (n.gaps.length) g.appendChild(el('circle', { class: 'gap-ring', cx: x + r - 2, cy: y - r + 2, r: 5 }));
      const tt = el('title');
      tt.textContent = `${n.name} — ${ROTULO_ST[n.st]} · etapa do fluxo (ordem real do dado)`;
      g.appendChild(tt);
      const entrar = (): void => o.aoSelecionar(n.external_id);
      g.addEventListener('click', entrar);
      g.addEventListener('keydown', (ev) => { if ((ev as KeyboardEvent).key === 'Enter' || (ev as KeyboardEvent).key === ' ') { ev.preventDefault(); entrar(); } });
      svg.appendChild(g);
    };
    const zona = (x: number, y: number, s: string): void => { txt(svg, x, y, s.toUpperCase().slice(0, 70), 'zone'); };

    if (!c.contexto || !c.grupos.length) {
      txt(svg, 40, 60, 'Este contexto nao possui grupos de fluxo (filhos process com etapas).', 'sl-text');
      txt(svg, 40, 82, 'A cena de processos deriva das etapas reais (child_kind = flow) na ordem do ordinal.', 'sl-text meta');
      raiz.appendChild(svg);
      return;
    }

    /* ---------------------------------------------- trilho PRIMARIO -------- */
    const x0 = 60, dxP = 138, yP = 110;
    if (c.primario) {
      const etapas = c.fluxoDe(c.primario.external_id);
      zona(40, 40, `${c.primario.name} · fluxo real · ${c.primario.external_id}`);
      svg.appendChild(el('path', { d: `M${x0} ${yP} H${x0 + dxP * Math.max(etapas.length - 1, 0)}`, class: 'track', 'marker-end': 'url(#parr)' }));
      etapas.forEach((n, i) => estacao(n, x0 + dxP * i, yP, 14, i, true, 22));
      /* relacoes REAIS por etapa: ator que aponta para a etapa; o que a etapa aponta */
      etapas.forEach((n, i) => {
        const x = x0 + dxP * i;
        for (const r of relacoes) {
          if (r.to === n.external_id && r.from !== c.primario!.external_id) {
            const ator = c.ativos.find((a) => a.external_id === r.from);
            if (!ator) continue;
            const ay = 200;
            svg.appendChild(el('path', { d: `M${x} ${ay - 18} V${yP + 20}`, class: 'track', 'marker-end': 'url(#parr)' }));
            const ga = el('g', { class: 'station actor-g', 'data-id': ator.external_id, tabindex: 0, role: 'button', 'aria-label': `${ator.name} — ${r.type} (relacao real)` });
            ga.appendChild(el('polygon', { points: `${x},${ay - 18} ${x + 18},${ay} ${x},${ay + 18} ${x - 18},${ay}`, class: 'actor' }));
            /* rotulo ABAIXO do losango: a coluna seguinte costuma abrigar o
               objeto filho da etapa (h06), e o texto a direita colidiria */
            txt(ga, x, ay + 32, `${trunc(ator.name, 22)} (${ator.node_type === 'partner' || ator.node_type === 'network' ? 'externo' : ator.node_type ?? 'no'}) — ${r.type}`, 'sl-text meta', { 'text-anchor': 'middle' });
            ga.addEventListener('click', () => o.aoSelecionar(ator.external_id));
            svg.appendChild(ga);
          }
          if (r.from === n.external_id) {
            const alvo = c.ativos.find((a) => a.external_id === r.to);
            if (!alvo) continue;
            txt(svg, x, yP + 52, `${r.type} → ${trunc(alvo.name, 16)} (relacao real)`, 'sl-text meta', { 'text-anchor': 'middle' });
          }
        }
        /* objeto sob a etapa: filho REAL nao-flow da etapa (ex.: h06 sob f02-8) */
        for (const filho of c.filhosDe(n.external_id).filter((f) => f.child_kind !== 'flow').slice(0, 1)) {
          const g = el('g', { class: 'nd bridge' + (selecionado === filho.external_id ? ' sel' : ''), 'data-id': filho.external_id, tabindex: 0, role: 'button', transform: `translate(${x - 88},${yP + 64})`, 'aria-label': `${filho.name} — filho de ${n.name}` });
          g.appendChild(el('rect', { class: 'card', width: 176, height: 44, rx: 8 }));
          txt(g, 12, 19, trunc(filho.name, 26), '');
          txt(g, 12, 34, `${filho.external_id} · ${filho.st} · filho de ${trunc(n.name, 14)}`, 'tp');
          g.addEventListener('click', () => o.aoSelecionar(filho.external_id));
          svg.appendChild(el('path', { d: `M${x} ${yP + 14} V${yP + 62}`, class: 'edge' }));
          svg.appendChild(g);
        }
      });
    }

    /* ----------------------------------------------- faixa de TRANSICAO ---- */
    let y = 300;
    if (c.transicao) {
      svg.appendChild(el('rect', { x: 40, y: y - 24, width: W - 80, height: 60, class: 'transition', rx: 8 }));
      txt(svg, 56, y - 6, 'TRANSICAO', 'zone');
      txt(svg, 56, y + 14, 'composicao da apresentacao · elo etapa-a-etapa nao comprovado', 'sl-text meta');
      if (c.de && c.alvo && c.para && c.entra && c.sai) {
        txt(svg, W - 56, y + 14, `relacao real: ${trunc(c.de.name, 22)} —${c.entra.type}→ ${trunc(c.alvo.name, 22)} —${c.sai.type}→ ${trunc(c.para.name, 22)}`, 'sl-text meta', { 'text-anchor': 'end' });
      } else {
        txt(svg, W - 56, y + 14, 'proveniencia em nivel de departamento: nao comprovada por relacao real', 'sl-text meta', { 'text-anchor': 'end' });
      }
      /* trilho do grupo de transicao */
      const ph = c.fluxoDe(c.transicao.external_id);
      const hy = 390;
      zona(40, hy - 40, `${c.transicao.name} · etapa do fluxo · ${c.transicao.external_id}`);
      svg.appendChild(el('path', { d: `M60 ${hy} H${60 + 120 * Math.max(ph.length - 1, 0)}`, class: 'track', 'marker-end': 'url(#parr)' }));
      ph.forEach((n, i) => estacao(n, 60 + 120 * i, hy, 11, null, true, 20));
      if (c.alvoRel && c.alvo) {
        txt(svg, 60 + 120 * ph.length + 10, hy + 4, `${c.alvoRel.type} → ${trunc(c.alvo.name, 20)} (relacao real)`, 'sl-text meta');
      }
      y = 490;
    } else {
      y = 300;
    }

    /* ------------------------------------------ demais grupos (trilhos) ---- */
    let yAnterior: number | null = c.transicao ? 390 : (c.primario ? yP : null);
    let xFimAnterior = c.transicao ? 60 + 120 * Math.max(c.fluxoDe(c.transicao.external_id).length - 1, 0) + 14 : x0 + dxP * Math.max((c.primario ? c.fluxoDe(c.primario.external_id).length : 1) - 1, 0) + 16;
    for (const g2 of c.demais) {
      const ops = c.fluxoDe(g2.external_id);
      const passo = ops.length > 10 ? 90 : 120;
      zona(40, y - 40, `${g2.name} · ${g2.external_id} · ${ops.length} etapas`);
      svg.appendChild(el('path', { d: `M60 ${y} H${60 + passo * Math.max(ops.length - 1, 0)}`, class: 'track comp' }));
      ops.forEach((n, i) => estacao(n, 60 + passo * i, y, 9, null, i % 2 === 0, 18));
      if (yAnterior !== null) {
        svg.appendChild(el('path', { d: `M${xFimAnterior} ${yAnterior} C 700 ${yAnterior} 700 ${y} ${60 - 14} ${y}`, class: 'track comp', 'marker-end': 'url(#parr)' }));
        txt(svg, W - 56, y + 34, 'seta tracejada = composicao visual, nao relacao de banco', 'sl-text meta', { 'text-anchor': 'end' });
      }
      yAnterior = y;
      xFimAnterior = 60 + passo * Math.max(ops.length - 1, 0) + 12;
      y += 100;
      if (y + 40 > H) { H = y + 40; svg.setAttribute('viewBox', `0 0 ${W} ${H}`); }
    }
    svg.style.aspectRatio = `${W}/${H}`;
    raiz.appendChild(svg);
  }

  return {
    render,
    mostrar(): void { raiz.hidden = false; },
    esconder(): void { raiz.hidden = true; },
    get visivel(): boolean { return !raiz.hidden; },
  };
}

export type Processos = ReturnType<typeof montarProcessos>;
